import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { PageFooter, styles } from './constants';
import { useTranslation } from 'react-i18next';
import { keys } from '../translator/translation_keys';
import {
  actionsKeys,
  operatorsList,
} from '../../components/question_menu/conditionalActions/conditionalAction.utils';
import { LexicalPdf } from './rich_text_pdf';

export const TemplateDocument = ({ inspectionTemplate }) => {
  const { t } = useTranslation();
  const fontSize = 10;
  const grayLight = '#abb5be';
  const textSecondary = '#6e6e73';
  const purple = '#6610f2';
  const localStyles = StyleSheet.create({
    text: { fontSize, marginBottom: 1, fontFamily: 'eina03semibold' },
    subtext: { fontSize: fontSize - 2, color: grayLight, marginBottom: 3 },
    headerText: {
      fontSize: fontSize + 5,
      fontFamily: 'eina03bold',
    },
    questionContainer: {
      borderBottom: 1,
      borderColor: grayLight,
      paddingBottom: 10,
      paddingTop: 5,
    },
    // conditional action styles
    conditionalBlock: {
      marginTop: 5,
      marginLeft: 12,
      paddingLeft: 6,
      borderLeft: `2px solid #e0e0e0`,
    },
    conditionLabel: {
      fontSize: fontSize - 2,
      color: textSecondary,
      fontFamily: 'eina03semibold',
      marginBottom: 2,
    },
    conditionalTag: {
      fontSize: fontSize - 2,
      color: purple,
      fontFamily: 'eina03semibold',
      backgroundColor: '#f3e8ff',
      paddingHorizontal: 4,
      paddingVertical: 1,
      marginBottom: 3,
      alignSelf: 'flex-start',
    },
    actionGroupLabel: {
      fontSize: fontSize - 2,
      color: textSecondary,
      fontFamily: 'eina03semibold',
      marginTop: 2,
      marginBottom: 1,
    },
    actionItem: {
      fontSize: fontSize - 1,
      color: '#1d1d1f',
      fontFamily: 'eina03regular',
      marginLeft: 4,
      marginBottom: 1,
    },
    actionItemBold: {
      fontSize: fontSize - 1,
      color: '#1d1d1f',
      fontFamily: 'eina03semibold',
      marginLeft: 4,
      marginBottom: 1,
    },
  });

  const formatConditionLabel = (ca) => {
    const operatorTitle =
      operatorsList.find((o) => o.key === ca.operator)?.title ?? ca.operator;
    const value = Array.isArray(ca.value)
      ? ca.value.join(', ')
      : ca.value ?? '';
    return `If answer ${operatorTitle.toLowerCase()} "${value}"`;
  };

  const ConditionalActions = ({ conditionalAction }) => {
    if (!Array.isArray(conditionalAction) || !conditionalAction.length)
      return null;

    return (
      <View style={localStyles.conditionalBlock}>
        {conditionalAction.map((ca, caIdx) => {
          const allActions = ca.actions ?? [];
          if (!allActions.length) return null;

          const groups = {
            questions: [],
            assessments: [],
            files: [],
            notifications: [],
            messages: [],
          };

          for (const { id, action, actionable, additionalData } of allActions) {
            switch (action) {
              case actionsKeys.followUpQuestion:
                if (actionable) groups.questions.push({ id, actionable });
                break;
              case actionsKeys.followUpTemplate:
                if (actionable) groups.assessments.push({ id, actionable });
                break;
              case actionsKeys.showFile:
                if (actionable) groups.files.push({ id, actionable });
                break;
              case actionsKeys.popUp:
              case actionsKeys.showMessage:
                groups.messages.push({ id, action, additionalData });
                break;
              case actionsKeys.sendNotification:
                if (actionable) groups.notifications.push({ id, actionable });
                break;
              default:
                break;
            }
          }

          return (
            <View key={`ca-${caIdx}`}>
              <Text style={localStyles.conditionalTag}>CONDITIONAL</Text>
              <Text style={localStyles.conditionLabel}>
                {formatConditionLabel(ca)}
              </Text>

              {groups.questions.length > 0 && (
                <View>
                  <Text style={localStyles.actionGroupLabel}>
                    Follow Up Questions
                  </Text>
                  {groups.questions.map(({ id, actionable }) => (
                    <View key={`tpl-q-${id}`}>
                      {actionable?.inputType === 'MESSAGE' ? (
                        <LexicalPdf content={actionable?.title} />
                      ) : (
                        <Text style={localStyles.actionItemBold}>
                          {actionable?.title}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}

              {groups.assessments.length > 0 && (
                <View>
                  <Text style={localStyles.actionGroupLabel}>
                    Follow Up Assessments
                  </Text>
                  {groups.assessments.map(({ id, actionable }) => (
                    <Text key={`tpl-a-${id}`} style={localStyles.actionItem}>
                      {actionable?.title}
                    </Text>
                  ))}
                </View>
              )}

              {groups.files.length > 0 && (
                <View>
                  <Text style={localStyles.actionGroupLabel}>Files</Text>
                  {groups.files.map(({ id, actionable }) => (
                    <Text key={`tpl-f-${id}`} style={localStyles.actionItem}>
                      {actionable?.name}
                    </Text>
                  ))}
                </View>
              )}

              {groups.notifications.length > 0 && (
                <View>
                  <Text style={localStyles.actionGroupLabel}>
                    Notifications
                  </Text>
                  {groups.notifications.map(({ id, actionable }) => (
                    <Text
                      key={`tpl-n-${id}`}
                      style={localStyles.actionItem}
                    >{`${actionable?.firstName} ${actionable?.lastName}`}</Text>
                  ))}
                </View>
              )}

              {groups.messages.length > 0 && (
                <View>
                  <Text style={localStyles.actionGroupLabel}>Messages</Text>
                  {groups.messages.map(({ id, action, additionalData }) =>
                    action === actionsKeys.showMessage ? (
                      <LexicalPdf
                        key={`tpl-m-${id}`}
                        content={additionalData?.message}
                      />
                    ) : (
                      <Text
                        key={`tpl-m-${id}`}
                        style={localStyles.actionItemBold}
                      >
                        {additionalData?.message}
                      </Text>
                    ),
                  )}
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Text style={localStyles.headerText}>{inspectionTemplate.title}</Text>
          <Text style={localStyles.text}>{inspectionTemplate.description}</Text>
        </View>
        <View style={styles.categoryContainer}>
          {inspectionTemplate.categories.map(
            ({ title, questions, id }, index) => (
              <View key={`${id}-${index}-${title}`}>
                <Text style={styles.categoryHeader}>{title}</Text>
                {questions.map((q) => (
                  <View key={q.id} style={localStyles.questionContainer}>
                    {q.inputType === 'MESSAGE' ? (
                      <LexicalPdf content={q.title} />
                    ) : (
                      <>
                        <Text style={localStyles.text}>{q.title}</Text>
                        <Text style={localStyles.subtext}>{q.description}</Text>
                        <Text style={localStyles.subtext}>
                          Question Type: {q.inputType}
                        </Text>
                        {!!q?.additionalData && (
                          <>
                            <Text style={localStyles.subtext}>Options</Text>
                            <Text style={localStyles.subtext}>
                              {q.additionalData}
                            </Text>
                          </>
                        )}
                      </>
                    )}
                    <ConditionalActions
                      conditionalAction={q.conditionalAction}
                    />
                  </View>
                ))}
              </View>
            ),
          )}
        </View>
        {!!inspectionTemplate.images?.length && (
          <View style={styles.categoryContainer}>
            <Text style={styles.categoryHeader}>
              {t(keys.common.REFERENCE_IMAGES)}
            </Text>
            <View style={styles.templateImageContainer}>
              {inspectionTemplate.images.map(({ imageUrl, index }) => (
                <Image
                  style={styles.templateImage}
                  src={imageUrl}
                  key={`${imageUrl.id}-${index}`}
                />
              ))}
            </View>
          </View>
        )}
        <PageFooter />
      </Page>
    </Document>
  );
};
